import React, { MouseEventHandler, useState } from "react";
import { ContentSpacer } from "../common/layout";
import { useListAgentsQuery } from "../../data/queries/list-agents";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { PlusIcon, Trash2Icon, PenIcon, EllipsisIcon, BotOffIcon } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "../../components/ui/dialog";
import AgentFormDialog from "./agent-form-dialog";
import { useDeleteAgentMutation } from "../../data/mutations/delete-agent";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { AgentMeta } from "tekkr-common/dist/model/agent/agent";
import { DateTime } from "luxon";
import { useOrg } from "../../auth/org-provider";
import { useGetAgentQuery } from "../../data/queries/get-agent";
import { UserAvatarAndName } from "../../components/shared/org/avatar-for-user";
import { getIconComponent } from "../../components/icon-picker";
import Spinner from "../../components/ui/spinner";
import { useConfirmDialog } from "../../components/modals/confirmation-dialog-provider";
import { useIsDemoOrg } from "../../lib/use-is-demo-org";
import { StoreIcon } from "lucide-react";

export function AgentsPage() {
    const query = useListAgentsQuery();
    const org = useOrg();
    const isDemoOrg = useIsDemoOrg();

    return (
        <ContentSpacer>
            <div className={"mt-8 flex flex-row items-center justify-between gap-4"}>
                <div>
                    <h2 className="flex items-center gap-2">Custom Agents</h2>
                    <p>Create and manage custom AI agents with specific instructions and capabilities.</p>
                </div>
                <AgentFormDialog>
                    <DialogTrigger asChild>
                        <Button>
                            <PlusIcon className="mr-2 h-4 w-4" />
                            Create agent
                        </Button>
                    </DialogTrigger>
                </AgentFormDialog>
            </div>

            {isDemoOrg && (
                <div className="mt-6 flex items-center gap-3 rounded-lg border border-border bg-muted/50 px-4 py-3">
                    <StoreIcon className="mb-1 h-5 w-5 shrink-0 text-muted-foreground" />
                    <div className="flex flex-col">
                        <p className="mb-1 text-sm font-semibold">Marketplace — Coming Soon</p>
                        <p className="text-sm text-muted-foreground">
                            Browse and install agents built by the Tekkr community.
                        </p>
                    </div>
                </div>
            )}

            <div className="mt-8">
                {query.isLoading && (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {[1, 2, 3].map((i) => (
                            <Card key={i}>
                                <CardHeader>
                                    <Skeleton className="h-6 w-3/4" />
                                    <Skeleton className="h-4 w-full" />
                                </CardHeader>
                                <CardContent>
                                    <Skeleton className="h-20 w-full" />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {query.isError && (
                    <div className="flex flex-col items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-4 text-center">
                        <BotOffIcon className="mb-1 h-6 w-6 text-destructive" />
                        <h3 className="text-lg font-semibold text-destructive">Failed to load agents</h3>
                        <p className="text-sm text-muted-foreground">
                            There was an error loading agents. Please try again later.
                        </p>
                    </div>
                )}

                {query.isSuccess && query.data.length === 0 && (
                    <div className={"flex w-full flex-col items-center gap-2 rounded-xl border p-8"}>
                        <BotOffIcon className={"h-6 w-6 text-muted-foreground"} />
                        <h3 className="text-lg font-semibold">No agents yet</h3>
                        <p className="text-center text-sm text-muted-foreground">
                            Create your first custom agent to get started.
                        </p>
                    </div>
                )}

                {query.isSuccess && query.data.length > 0 && (
                    <div className={"flex flex-col gap-3"}>
                        <p className={"text-sm font-semibold text-muted-foreground"}>Agents at {org.name}</p>
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {query.data.map((agent) => (
                                <AgentCard key={agent.id} agent={agent} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </ContentSpacer>
    );
}

interface AgentCardProps {
    agent: AgentMeta;
}

function AgentCard({ agent }: AgentCardProps) {
    const deleteMutation = useDeleteAgentMutation();
    const confirm = useConfirmDialog();

    const handleDelete: MouseEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault();
        confirm(
            {
                title: "Delete Agent",
                body: (
                    <div className={"flex flex-col gap-3 pb-3"}>
                        <div>
                            Are you sure you want to delete the agent{" "}
                            <strong className={"font-semibold"}>{agent.name}</strong>?
                        </div>
                        <div className={"text-sm text-muted-foreground"}>This action cannot be undone.</div>
                    </div>
                ),
                confirmButtonText: "Delete",
                confirmButtonVariant: "destructive",
            },
            () => {
                deleteMutation.mutate(agent.id);
            }
        )();
    };

    const [editing, setEditing] = useState<boolean>(false);
    const handleEdit = () => {
        setEditing(true);
    };

    const createdAt = DateTime.fromISO(agent.createdAt).toRelative();
    const AgentIcon = getIconComponent(agent.icon);

    return (
        <>
            <div className={"flex flex-col items-stretch gap-4 rounded-lg border bg-card p-4"}>
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <AgentIcon className="h-5 w-5 shrink-0" />
                            <div className={"line-clamp-1"}>{agent.name}</div>
                        </CardTitle>
                        <CardDescription className="mt-1 line-clamp-3">
                            {agent.description || "No description"}
                        </CardDescription>
                    </div>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                                <EllipsisIcon className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleEdit}>
                                <PenIcon className="mr-2 h-4 w-4" />
                                Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleDelete} className={"text-destructive"}>
                                <Trash2Icon className="mr-2 h-4 w-4" />
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                <div className="flex flex-row flex-wrap items-center gap-1 text-xs text-muted-foreground">
                    Created {createdAt} by{" "}
                    <UserAvatarAndName avatarClassName={"w-4 h-4 ms-0.5"} userId={agent.creatorId} />
                </div>
            </div>
            {editing && <EditAgentDialog onClose={() => setEditing(false)} agentId={agent.id} />}
        </>
    );
}

function EditAgentDialog(props: { agentId: string; onClose: () => void }) {
    const agentQuery = useGetAgentQuery(props.agentId);
    const handleOpenChange = (open: boolean) => {
        if (!open) {
            props.onClose();
        }
    };
    if (!agentQuery.data && !agentQuery.isError) {
        return (
            <Dialog open={true}>
                <DialogContent disableClose={true}>
                    <div className={"flex flex-row items-center justify-center gap-3 text-sm text-muted-foreground"}>
                        <Spinner />
                        Loading agent..
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    if (agentQuery.data) {
        return (
            <AgentFormDialog
                onSuccess={props.onClose}
                open={true}
                onOpenChange={handleOpenChange}
                editMode={{ agent: agentQuery.data }}
            >
                <DialogTrigger asChild></DialogTrigger>
            </AgentFormDialog>
        );
    }

    return null;
}
